const CYRILLIC = ['ru', 'uk'];

export const GLYPH_BLOCKS = {
  de: 'ÄÖÜäöüß',
  es: 'ÑñÁÉÍÓÚÜ¡¿',
  pl: 'ĄĆĘŁŃÓŚŹŻąćęłńóśźż',
  cs: 'ÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ áčďéěíňóřšťúůýž',
  lt: 'ĄČĘĖĮŠŲŪŽ ąčęėįšųūž',
  lv: 'ĀČĒĢĪĶĻŅŠŪŽ āčēģīķļņšūž',
  sv: 'ÅÄÖåäö',
  ru: 'АБВГДЕЁЖЗИЙКЛМНОПРСТУФХЦЧШЩЪЫЬЭЮЯ абвгдеёжзийклмнопрстуфхцчшщъыьэюя',
  uk: 'АБВГДЕЖЗИІЇЙКЛМНОПРСТУФХЦЧШЩЬЮЯ абвгдежзиіїйклмнопрстуфхцчшщьюя ЄєҐґ',
};

// Verified against the Google Fonts CSS subsets on 2026-07-31. Metadata wins
// over the starter expectation: Pacifico currently publishes a Cyrillic subset.
const CYRILLIC_FONTS = new Set([
  'Playfair Display', 'Prata', 'PT Serif', 'Lora', 'Literata', 'Cormorant', 'Alegreya',
  'Montserrat', 'Inter', 'Manrope', 'Rubik', 'Jost', 'Raleway',
  'Unbounded', 'Oswald', 'Amatic SC', 'Comfortaa',
  'Caveat', 'Marck Script', 'Pacifico',
]);

export const FONT_CATALOG = [
  ['serif', 'Playfair Display'], ['serif', 'Prata'], ['serif', 'PT Serif'], ['serif', 'Lora'],
  ['serif', 'Literata'], ['serif', 'Cormorant'], ['serif', 'Alegreya'],
  ['sans', 'Montserrat'], ['sans', 'Inter'], ['sans', 'Manrope'], ['sans', 'Rubik'],
  ['sans', 'Jost'], ['sans', 'Raleway'],
  ['display', 'Unbounded'], ['display', 'Oswald'], ['display', 'Bebas Neue'],
  ['display', 'Anton'], ['display', 'Amatic SC'], ['display', 'Comfortaa'],
  ['script', 'Caveat'], ['script', 'Marck Script'], ['script', 'Pacifico'],
].map(([group, family]) => ({
  group,
  family,
  coverage: CYRILLIC_FONTS.has(family) ? ['latin', 'cyrillic'] : ['latin'],
}));

const fontLoads = new Map();

export function normalizeLanguage(value) {
  return String(value || '').toLowerCase().split(/[-_]/)[0];
}

export function collectTypographyLanguages(brandProfile, socialAccounts = []) {
  const values = [
    brandProfile?.language,
    brandProfile?.defaultPublishLanguage,
    ...socialAccounts.flatMap((account) => [account?.publishLanguage, account?.defaultPublishLanguage]),
  ].map(normalizeLanguage).filter(Boolean);
  return [...new Set(values)].sort();
}

export function languagesNeedCyrillic(languages) {
  return languages.some((language) => CYRILLIC.includes(normalizeLanguage(language)));
}

export function previewLetters(language) {
  const code = normalizeLanguage(language);
  return `Aa${GLYPH_BLOCKS[code] ? ` · ${GLYPH_BLOCKS[code]}` : ''}`;
}

export function specimenGlyphs(languages) {
  const blocks = [...new Set(languages.map(normalizeLanguage).filter(Boolean))]
    .map((language) => GLYPH_BLOCKS[language])
    .filter(Boolean);
  return ['ABCDEFGHIJKLMNOPQRSTUVWXYZ', 'abcdefghijklmnopqrstuvwxyz', ...blocks, '0123456789'];
}

export async function loadGoogleFont(family, text) {
  const key = `${family}\n${text}`;
  if (fontLoads.has(key)) return fontLoads.get(key);
  const promise = (async () => {
    if (typeof FontFace === 'undefined' || !document?.fonts) throw new Error('FontFace API unavailable');
    const familyQuery = family.trim().replace(/\s+/g, '+');
    const cssUrl = `https://fonts.googleapis.com/css2?family=${familyQuery}&display=swap&text=${encodeURIComponent(text)}`;
    const response = await fetch(cssUrl);
    if (!response.ok) throw new Error(`Google Fonts CSS ${response.status}`);
    const css = await response.text();
    // A `text=`-subsetted request answers with an extension-less dynamic URL
    // (https://fonts.gstatic.com/l/font?kit=…), so matching on `.woff2` finds
    // nothing and every preview silently falls back to the default face.
    const source = css.match(/url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/)?.[1];
    if (!source) throw new Error('Google Fonts woff2 not found');
    const face = new FontFace(family, `url(${source}) format("woff2")`, { weight: '400', style: 'normal' });
    await face.load();
    document.fonts.add(face);
    return face;
  })();
  fontLoads.set(key, promise);
  try { return await promise; }
  catch (error) { fontLoads.delete(key); throw error; }
}

function fitFont(ctx, text, family, maxWidth, startSize) {
  let size = startSize;
  do {
    ctx.font = `400 ${size}px "${family}"`;
    if (ctx.measureText(text).width <= maxWidth) return size;
    size -= 2;
  } while (size > 24);
  return size;
}

export function renderTypographySpecimen({ family, brandName, languages, brandColors }) {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext('2d');
  const accent = brandColors.find((color) => /^#[0-9a-f]{6}$/i.test(color)) || '#8B7AFF';
  ctx.fillStyle = '#11131A';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, `${accent}55`);
  gradient.addColorStop(0.55, `${accent}12`);
  gradient.addColorStop(1, '#11131A00');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const lines = specimenGlyphs(languages);
  ctx.fillStyle = '#F7F7FA';
  ctx.textBaseline = 'middle';
  const name = brandName || 'Your brand';
  fitFont(ctx, name, family, 880, 116);
  ctx.fillText(name, 100, 210);
  ctx.fillStyle = '#D9D9E2';
  const lineGap = Math.min(112, 660 / Math.max(1, lines.length));
  lines.forEach((line, index) => {
    fitFont(ctx, line, family, 880, lineGap < 70 ? 30 : (index < 2 ? 48 : 42));
    ctx.fillText(line, 100, 400 + (index * lineGap));
  });
  ctx.fillStyle = accent;
  ctx.fillRect(100, 1120, 160, 8);
  ctx.fillStyle = '#8E91A3';
  ctx.font = '400 24px sans-serif';
  ctx.fillText(family, 100, 1190);
  return canvas.toDataURL('image/png');
}

export function defaultTypographyProfile(brandColors = []) {
  return {
    casing: 'upper',
    accentTreatment: 'italic-accent',
    accentColor: brandColors[0] || '#E8742C',
    placement: 'top',
  };
}
